import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { NetworkProvider } from '@/lib/network/NetworkProvider';
import AndroidBackHandler from '@/components/resqly/AndroidBackHandler';
import { Toaster } from '@/components/ui/sonner';
import PrivateRoute from '@/components/PrivateRoute';
import Splash from '@/components/Splash';

// Consumer
import ConsumerLogin from '@/pages/consumer/ConsumerLogin';
import ConsumerOnboarding from '@/pages/consumer/ConsumerOnboarding';
import ConsumerHome from '@/pages/consumer/ConsumerHome';
import RangerIntroduction from '@/pages/consumer/RangerIntroduction';
import ConsumerServiceDetail from '@/pages/consumer/ConsumerServiceDetail';
import ConsumerPharmacy from '@/pages/consumer/ConsumerPharmacy';
import ConsumerWaitlist from '@/pages/consumer/ConsumerWaitlist';
import ConsumerEmergency from '@/pages/consumer/ConsumerEmergency';
import ConsumerProfile from '@/pages/consumer/ConsumerProfile';
import ConsumerNotifications from '@/pages/consumer/ConsumerNotifications';
import ConsumerFamily from '@/pages/consumer/ConsumerFamily';
import ConsumerPrescriptions from '@/pages/consumer/ConsumerPrescriptions';
import ConsumerLabTests from '@/pages/consumer/ConsumerLabTests';
import ConsumerEmergencyRequest from '@/pages/consumer/ConsumerEmergencyRequest';
import LocationPicker from '@/pages/consumer/LocationPicker';
import SOSVault from '@/pages/consumer/SOSVault';
import BroadcastSearchingScreen from '@/components/broadcast/BroadcastSearchingScreen';
import ConsumerBookingPayment from '@/pages/consumer/ConsumerBookingPayment';
import ConsumerBookingExtraPayment from '@/pages/consumer/ConsumerBookingExtraPayment';
import ConsumerBookingTracking from '@/pages/consumer/ConsumerBookingTracking';
import ConsumerBookings from '@/pages/consumer/ConsumerBookings';
import ConsumerBookingDetail from '@/pages/consumer/ConsumerBookingDetail';

// Provider
import ProviderLogin from '@/pages/provider/ProviderLogin';
import ProviderCategory from '@/pages/provider/ProviderCategory';
import ProviderKYC from '@/pages/provider/ProviderKYC';
import ProviderStatus from '@/pages/provider/ProviderStatus';
import ProviderDashboard from '@/pages/provider/ProviderDashboard';
import ProviderOrders from '@/pages/provider/ProviderOrders';
import ProviderEarnings from '@/pages/provider/ProviderEarnings';
import ProviderReviews from '@/pages/provider/ProviderReviews';
import ProviderDocuments from '@/pages/provider/ProviderDocuments';
import ProviderProfile from '@/pages/provider/ProviderProfile';
import ProviderSupport from '@/pages/provider/ProviderSupport';
import ProviderOffers from '@/pages/provider/ProviderOffers';
import ProviderOfferDetail from '@/pages/provider/ProviderOfferDetail';
import ProviderBookingDetail from '@/pages/provider/ProviderBookingDetail';
import ProviderPlans from '@/pages/provider/ProviderPlans';
import ProviderPharmacy from '@/pages/provider/ProviderPharmacy';
import ProviderLabs from '@/pages/provider/ProviderLabs';

// Admin
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminFinancialConfig from '@/pages/admin/AdminFinancialConfig';
import AdminPayouts from '@/pages/admin/AdminPayouts';
import AdminProviderDetail from '@/pages/admin/AdminProviderDetail';
import AdminServices from '@/pages/admin/AdminServices';
import AdminServicePricing from '@/pages/admin/AdminServicePricing';
import AdminPharmacyPricing from '@/pages/admin/AdminPharmacyPricing';
import AdminProviderDispatch from '@/pages/admin/AdminProviderDispatch';
import AdminReferrals from '@/pages/admin/AdminReferrals';
import AdminPaymentOperations from '@/pages/admin/AdminPaymentOperations';
import AdminDesktopShell from '@/components/AdminDesktopShell';
import AdminTeam from '@/pages/admin/AdminTeam';
import AdminSecurity from '@/pages/admin/AdminSecurity';
import AdminProviders from '@/pages/admin/AdminProviders';
import AdminOperations from '@/pages/admin/AdminOperations';
import AdminCustomers from '@/pages/admin/AdminCustomers';
import AdminAudit from '@/pages/admin/AdminAudit';
import AdminRecover from '@/pages/admin/AdminRecover';
import AdminPharmacy from '@/pages/admin/AdminPharmacy';
import AdminLabs from '@/pages/admin/AdminLabs';
import ReferralLanding from '@/pages/referral/ReferralLanding';
import ConsumerReferral from '@/pages/consumer/ConsumerReferral';
import ProviderReferral from '@/pages/provider/ProviderReferral';

// Compliance
import Privacy from '@/pages/compliance/Privacy';
import Terms from '@/pages/compliance/Terms';
import Support from '@/pages/compliance/Support';
import Contact from '@/pages/compliance/Contact';
import DeleteAccount from '@/pages/compliance/DeleteAccount';
import Permissions from '@/pages/compliance/Permissions';

import '@/App.css';

function RootRedirect() {
  // Consumer is the default experience. If already logged in as consumer, go to home.
  // Otherwise, go to consumer login.
  const { token, role, loading } = useAuth();
  if (loading) return null;
  if (token && role === 'consumer') return <Navigate to="/consumer/home" replace />;
  if (token && role === 'provider') return <Navigate to="/provider/dashboard" replace />;
  if (token && role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  return <Navigate to="/consumer/login" replace />;
}

function App() {
  const [splashed, setSplashed] = useState(() => sessionStorage.getItem('resqly_splashed') === '1');
  useEffect(() => {
    if (splashed) sessionStorage.setItem('resqly_splashed', '1');
  }, [splashed]);

  return (
    <AuthProvider>
      <NetworkProvider>
      {!splashed && <Splash onDone={() => { sessionStorage.setItem('resqly_splashed', '1'); setSplashed(true); }} />}
      <BrowserRouter>
        <AndroidBackHandler />
        <Routes>
          <Route path="/" element={<RootRedirect />} />

          {/* Consumer */}
          <Route path="/consumer/login" element={<ConsumerLogin />} />
          <Route path="/consumer/onboarding" element={<PrivateRoute allow={['consumer']}><ConsumerOnboarding /></PrivateRoute>} />
          <Route path="/consumer/home" element={<PrivateRoute allow={['consumer']}><ConsumerHome /></PrivateRoute>} />
          <Route path="/consumer/ranger" element={<PrivateRoute allow={['consumer']}><RangerIntroduction /></PrivateRoute>} />
          <Route path="/consumer/service/:serviceKey" element={<PrivateRoute allow={['consumer']}><ConsumerServiceDetail /></PrivateRoute>} />
          <Route path="/consumer/pharmacy" element={<PrivateRoute allow={['consumer']}><ConsumerPharmacy /></PrivateRoute>} />
          <Route path="/consumer/waitlist" element={<PrivateRoute allow={['consumer']}><ConsumerWaitlist /></PrivateRoute>} />
          <Route path="/consumer/emergency" element={<PrivateRoute allow={['consumer']}><ConsumerEmergency /></PrivateRoute>} />
          <Route path="/consumer/emergency-request" element={<PrivateRoute allow={['consumer']}><ConsumerEmergencyRequest /></PrivateRoute>} />
          <Route path="/consumer/profile" element={<PrivateRoute allow={['consumer']}><ConsumerProfile /></PrivateRoute>} />
          <Route path="/consumer/referral" element={<PrivateRoute allow={['consumer']}><ConsumerReferral /></PrivateRoute>} />
          <Route path="/consumer/notifications" element={<PrivateRoute allow={['consumer']}><ConsumerNotifications /></PrivateRoute>} />
          <Route path="/consumer/family" element={<PrivateRoute allow={['consumer']}><ConsumerFamily /></PrivateRoute>} />
          <Route path="/consumer/prescriptions" element={<PrivateRoute allow={['consumer']}><ConsumerPrescriptions /></PrivateRoute>} />
          <Route path="/consumer/lab-tests" element={<PrivateRoute allow={['consumer']}><ConsumerLabTests /></PrivateRoute>} />
          <Route path="/consumer/location" element={<PrivateRoute allow={['consumer']}><LocationPicker /></PrivateRoute>} />
          <Route path="/consumer/broadcast/:requestId" element={<PrivateRoute allow={['consumer']}><BroadcastSearchingScreen /></PrivateRoute>} />
          <Route path="/consumer/bookings" element={<PrivateRoute allow={['consumer']}><ConsumerBookings /></PrivateRoute>} />
          <Route path="/consumer/bookings/:bookingId" element={<PrivateRoute allow={['consumer']}><ConsumerBookingDetail /></PrivateRoute>} />
          <Route path="/consumer/bookings/:bookingId/payment" element={<PrivateRoute allow={['consumer']}><ConsumerBookingPayment /></PrivateRoute>} />
          <Route path="/consumer/bookings/:bookingId/extra-payment" element={<PrivateRoute allow={['consumer']}><ConsumerBookingExtraPayment /></PrivateRoute>} />
          <Route path="/consumer/bookings/:bookingId/tracking" element={<PrivateRoute allow={['consumer']}><ConsumerBookingTracking /></PrivateRoute>} />

          {/* Provider */}
          <Route path="/provider/login" element={<ProviderLogin />} />
          <Route path="/provider/category" element={<PrivateRoute allow={['provider']}><ProviderCategory /></PrivateRoute>} />
          <Route path="/provider/kyc" element={<PrivateRoute allow={['provider']}><ProviderKYC /></PrivateRoute>} />
          <Route path="/provider/status" element={<PrivateRoute allow={['provider']}><ProviderStatus /></PrivateRoute>} />
          <Route path="/provider/dashboard" element={<PrivateRoute allow={['provider']}><ProviderDashboard /></PrivateRoute>} />
          <Route path="/provider/referral" element={<PrivateRoute allow={['provider']}><ProviderReferral /></PrivateRoute>} />
          <Route path="/provider/orders" element={<PrivateRoute allow={['provider']}><ProviderOrders /></PrivateRoute>} />
          <Route path="/provider/earnings" element={<PrivateRoute allow={['provider']}><ProviderEarnings /></PrivateRoute>} />
          <Route path="/provider/reviews" element={<PrivateRoute allow={['provider']}><ProviderReviews /></PrivateRoute>} />
          <Route path="/provider/documents" element={<PrivateRoute allow={['provider']}><ProviderDocuments /></PrivateRoute>} />
          <Route path="/provider/profile" element={<PrivateRoute allow={['provider']}><ProviderProfile /></PrivateRoute>} />
          <Route path="/provider/support" element={<PrivateRoute allow={['provider']}><ProviderSupport /></PrivateRoute>} />
          <Route path="/provider/offers" element={<PrivateRoute allow={['provider']}><ProviderOffers /></PrivateRoute>} />
          <Route path="/provider/offers/:offerId" element={<PrivateRoute allow={['provider']}><ProviderOfferDetail /></PrivateRoute>} />
          <Route path="/provider/plans" element={<PrivateRoute allow={['provider']}><ProviderPlans /></PrivateRoute>} />
          <Route path="/provider/pharmacy" element={<PrivateRoute allow={['provider']}><ProviderPharmacy /></PrivateRoute>} />
          <Route path="/provider/labs" element={<PrivateRoute allow={['provider']}><ProviderLabs /></PrivateRoute>} />
          <Route path="/provider/bookings/:bookingId" element={<PrivateRoute allow={['provider']}><ProviderBookingDetail /></PrivateRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/recover" element={<AdminRecover />} />
          <Route path="/admin/team" element={<PrivateRoute allow={['admin']} adminPermission="team_access"><AdminDesktopShell><AdminTeam /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/security" element={<PrivateRoute allow={['admin']}><AdminDesktopShell><AdminSecurity /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/dashboard" element={<PrivateRoute allow={['admin']} adminPermission="dashboard"><AdminDesktopShell><AdminDashboard /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/operations" element={<PrivateRoute allow={['admin']} adminPermission="operations"><AdminDesktopShell><AdminOperations /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/customers" element={<PrivateRoute allow={['admin']} adminPermission="customers"><AdminDesktopShell><AdminCustomers /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/audit" element={<PrivateRoute allow={['admin']} adminPermission="audit"><AdminDesktopShell><AdminAudit /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/providers" element={<PrivateRoute allow={['admin']} adminPermission="providers"><AdminDesktopShell><AdminProviders /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/providers/:id" element={<PrivateRoute allow={['admin']} adminPermission="providers"><AdminDesktopShell><AdminProviderDetail /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/services" element={<PrivateRoute allow={['admin']} adminPermission="services"><AdminDesktopShell><AdminServices /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/services/:serviceKey/pricing" element={<PrivateRoute allow={['admin']} adminPermission="services"><AdminDesktopShell><AdminServicePricing /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/pharmacy" element={<PrivateRoute allow={['admin']} adminPermission="pharmacy"><AdminDesktopShell><AdminPharmacy /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/pharmacy/pricing" element={<PrivateRoute allow={['admin']} adminPermission="pharmacy"><AdminDesktopShell><AdminPharmacyPricing /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/labs" element={<PrivateRoute allow={['admin']} adminPermission="labs"><AdminDesktopShell><AdminLabs /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/provider-dispatch" element={<PrivateRoute allow={['admin']} adminPermission="dispatch"><AdminDesktopShell><AdminProviderDispatch /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/referrals" element={<PrivateRoute allow={['admin']} adminPermission="referrals"><AdminDesktopShell><AdminReferrals /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/payment-operations" element={<PrivateRoute allow={['admin']} adminPermission="payments"><AdminDesktopShell><AdminPaymentOperations /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/financial-config" element={<PrivateRoute allow={['admin']} adminPermission="finance"><AdminDesktopShell><AdminFinancialConfig /></AdminDesktopShell></PrivateRoute>} />
          <Route path="/admin/payouts" element={<PrivateRoute allow={['admin']} adminPermission="finance"><AdminDesktopShell><AdminPayouts /></AdminDesktopShell></PrivateRoute>} />

          <Route path="/r/:code" element={<ReferralLanding />} />

          {/* Compliance */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/support" element={<Support />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/permissions" element={<Permissions />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
      </NetworkProvider>
    </AuthProvider>
  );
}

export default App;
